// src/EventModal.js

import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select'; // Useremo react-select per la condivisione
import DatePicker from 'react-datepicker'; // Useremo react-datepicker per selezionare data/ora
import 'react-datepicker/dist/react-datepicker.css';
import { useUser } from './UserContext'; // Per ottenere l'utente corrente e la lista staff
import { db } from './firebase'; // Importa db per caricare lo staff
import { collection, getDocs } from 'firebase/firestore'; // Per caricare lo staff

// Stili base per il modale (simili agli altri modali)
const modalStyles = {
     position: 'fixed', zIndex: 2001, left: 0, top: 0, width: '100%', height: '100%',
     backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center'
};
const modalContentStyles = {
     backgroundColor: '#fefefe', padding: '25px', border: '1px solid #888',
     width: '90%', maxWidth: '600px', borderRadius: '8px',
     maxHeight: '90vh', overflowY: 'auto' // Aggiunto per gestire schermi piccoli
};
// Stili per react-select e react-datepicker dentro il modale
const selectStyles = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: base => ({...base, marginTop: '5px'}),
};
const datePickerWrapperStyle = {
    width: '100%',
};
const recurrenceOptions = [
    { value: 'none', label: 'Non si ripete' },
    { value: 'weekly', label: 'Settimanale' },
    { value: 'monthly', label: 'Mensile' },
    { value: 'yearly', label: 'Annuale' },
];

export function EventModal({ eventInfo, onSave, onClose, onDelete, staffList }) {
    const { userProfile } = useUser();
    
    // Stati del form
    const [title, setTitle] = useState(eventInfo?.title || '');
    const [start, setStart] = useState(eventInfo?.start ? moment(eventInfo.start).toDate() : new Date());
    const [end, setEnd] = useState(eventInfo?.end ? moment(eventInfo.end).toDate() : moment().add(1, 'hour').toDate());
    const [allDay, setAllDay] = useState(eventInfo?.allDay || false);
    const [eventType, setEventType] = useState(eventInfo?.type || 'shared');
    
    // --- NUOVO STATO PER LE NOTE ---
    const [notes, setNotes] = useState(eventInfo?.notes || ''); 
    // -------------------------------

    const [sharedWith, setSharedWith] = useState(() => {
        if (eventInfo?.type === 'personal' && eventInfo.sharedWith && staffList?.length > 0) {
            return staffList.filter(user => eventInfo.sharedWith.includes(user.value));
        }
        return [];
    });
    const [recurrenceRule, setRecurrenceRule] = useState(eventInfo?.recurrenceRule || 'none');

    const canEditShared = userProfile?.permissions?.calendar in ['completo', 'semi-completo'] || userProfile?.role === 'Super Admin';
    const isEditing = eventInfo && eventInfo.id;
    const isOwner = isEditing && eventInfo.ownerId === userProfile.uid; 

    // Permessi
    const canEditThisEvent = isOwner || (eventInfo?.type === 'shared' && canEditShared);
    const canDeleteThisEvent = isOwner || (eventInfo?.type === 'shared' && (userProfile?.permissions?.calendar === 'completo' || userProfile?.role === 'Super Admin'));

    const handleSave = () => {
        if (!title.trim()) {
            alert('Il titolo è obbligatorio.');
            return;
        }
        if (!allDay && moment(end).isSameOrBefore(moment(start))) {
            alert("L'orario di fine deve essere successivo all'orario di inizio.");
            return;
        }

        const eventData = {
            id: eventInfo?.id,
            title: title.trim(),
            start: start,
            end: end,
            allDay: allDay,
            type: eventType,
            sharedWith: eventType === 'personal' ? sharedWith.map(user => user.value) : [],
            recurrenceRule: recurrenceRule,
            ownerId: eventInfo?.ownerId,
            ownerName: eventInfo?.ownerName,
            
            // --- SALVATAGGIO NOTE ---
            notes: notes.trim(),
            // ------------------------
        };
        onSave(eventData);
    };

    const handleStartDateChange = (date) => {
        setStart(date);
        if (moment(end).isBefore(moment(date))) {
            setEnd(moment(date).add(1, 'hour').toDate());
        }
    };
    const handleEndDateChange = (date) => {
        setEnd(date);
    };

    const showSharedByInfo = eventInfo?.type === 'personal' && !isOwner && eventInfo?.ownerName;

    return (
        <div style={modalStyles} onMouseDown={onClose}>
            <div style={modalContentStyles} onMouseDown={e => e.stopPropagation()}>
                {showSharedByInfo && (
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', textAlign: 'right', marginBottom: '-10px', marginTop: '-10px' }}>
                        Condiviso da: <strong>{eventInfo.ownerName}</strong>
                    </p>
                )}

                <h3>{isEditing ? 'Modifica Evento' : 'Nuovo Evento'}</h3>

                <div style={{ textAlign: 'left', marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {/* Campo Titolo */}
                    <div className="form-group">
                        <label htmlFor="event-title">Titolo*</label>
                        <input
                            id="event-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Es. Appuntamento, Promemoria..."
                             disabled={isEditing && !canEditThisEvent}
                        />
                    </div>

                    {/* Checkbox Tutto il Giorno */}
                    <div className="form-group-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={allDay}
                                onChange={(e) => setAllDay(e.target.checked)}
                                disabled={isEditing && !canEditThisEvent}
                            />
                            Tutto il giorno
                        </label>
                    </div>

                    {/* Selettori Data/Ora Inizio e Fine */}
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Inizio</label>
                            <DatePicker
                                selected={start}
                                onChange={handleStartDateChange}
                                showTimeSelect={!allDay}
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat={allDay ? "dd/MM/yyyy" : "dd/MM/yyyy HH:mm"}
                                locale="it"
                                className="form-control"
                                wrapperClassName="datePickerWrapperStyle"
                                disabled={isEditing && !canEditThisEvent}
                                // Fix visualizzazione per il primo datepicker
                                popperProps={{ strategy: 'fixed' }}
                            />
                        </div>
                        {recurrenceRule === 'none' && (
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Fine</label>
                                <DatePicker
                                    selected={end}
                                    onChange={handleEndDateChange}
                                    showTimeSelect={!allDay}
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat={allDay ? "dd/MM/yyyy" : "dd/MM/yyyy HH:mm"}
                                    minDate={start}
                                    minTime={!allDay && moment(start).isSame(moment(end), 'day') ? start : null}
                                    maxTime={!allDay && moment(start).isSame(moment(end), 'day') ? moment(start).endOf('day').toDate() : null}
                                    locale="it"
                                    className="form-control"
                                    wrapperClassName="datePickerWrapperStyle"
                                    disabled={isEditing && !canEditThisEvent}
                                    
                                    // *** FIX POSIZIONAMENTO ***
                                    // bottom-end fa aprire il calendario allineato a DESTRA, evitando che venga tagliato
                                    popperPlacement="bottom-end" 
                                    popperProps={{ strategy: 'fixed' }}
                                    // **************************
                                />
                            </div>
                        )}
                    </div> 

                     {/* Campo Ricorrenza */}
                    <div className="form-group">
                        <label>Ricorrenza</label>
                        <Select
                            options={recurrenceOptions}
                            value={recurrenceOptions.find(opt => opt.value === recurrenceRule)}
                            onChange={(selectedOption) => setRecurrenceRule(selectedOption.value)}
                            styles={selectStyles}
                            isDisabled={isEditing && !canEditThisEvent}
                        />
                    </div>

                    {/* Selettore Tipo Evento */}
                    <div className="form-group">
                        <label>Tipo Evento</label>
                        <select
                            value={eventType}
                            onChange={(e) => setEventType(e.target.value)}
                            disabled={isEditing && !isOwner}
                        >
                            <option value="shared">Condiviso (Visibile a tutti)</option>
                            <option value="personal">Personale</option>
                        </select>
                    </div>

                    {/* Selettore Condivisione */}
                    {eventType === 'personal' && (
                        <div className="form-group">
                            <label>Condividi con (opzionale)</label>
                            <Select
                                isMulti
                                options={staffList} 
                                value={sharedWith}
                                onChange={setSharedWith}
                                placeholder="Seleziona utenti..."
                                styles={selectStyles}
                                isDisabled={isEditing && !isOwner}
                            />
                        </div>
                    )}

                    {/* --- NUOVO CAMPO NOTE --- */}
                    <div className="form-group">
                        <label>Note (Opzionale)</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={isEditing && !canEditThisEvent}
                            placeholder="Aggiungi dettagli..."
                            rows="3"
                            style={{ 
                                width: '100%', 
                                padding: '8px', 
                                borderRadius: '4px', 
                                border: '1px solid #ccc',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                        />
                    </div>
                    {/* --- FINE CAMPO NOTE --- */}

                </div>

                {/* Pulsanti Azione */}
                <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <button
                            onClick={handleSave}
                            className="modal-button confirm-button"
                            disabled={isEditing && !canEditThisEvent}
                        >
                            {isEditing ? 'Salva Modifiche' : 'Crea Evento'}
                        </button>
                        <button onClick={onClose} className="modal-button cancel-button">
                            Annulla
                        </button>
                    </div>
                    {isEditing && canDeleteThisEvent && (
                        <button onClick={() => onDelete(eventInfo.id)} className="modal-button delete-button">
                            Elimina
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}